import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types (mirror FabricReportData from web) ────────────────────────────────
export interface FabricReportPDFData {
  id: string;
  result: string;
  total_penalty_points: number;
  total_defects: number;
  rolls_inspected: number;
  created_at: string;
  inspector_name: string;
  supplier: string;
  buyer_name: string;
  po_number: string;
  style_number: string;
  fabric_type: string;
  color_shade: string;
  total_yards: number;
  composition: any;
  pantone: any;
  target_gsm: number;
  selvedge: number;
  length: number;
  width: number;
  target_epi: number;
  original_epi: number;
  target_ppi: number;
  original_ppi: number;
  inspection_standard: string;
  aql_points: number;
  lot_size: number;
  sample_size: number;
  overall_remarks: string;
  recommendation: string;
  qa_signature_url: string;
  factory_signature_url: string;
  inspection_basis?: string;
  checklists: Array<{
    title: string;
    status: string;
    remarks: string;
    evidence_urls: string[];
  }>;
  defects: Array<{
    roll_number: number;
    points: number;
    type: string;
    category?: string;
    defect_type?: string;
    description: string;
    yard_number: string;
    image_urls: string[];
  }>;
  photos: Array<{
    id: string;
    label: string;
    image_url: string;
    is_required: boolean;
  }>;
  gsm_samples?: Array<{ sample_size?: number; original_gsm?: number; sampleSize?: number; originalGsm?: number }>;
  gsmSamples?: Array<any>;
  roll_width_samples?: Array<{ length_point?: number | null; value?: number | null; unit?: string }>;
  bow_skew_samples?: Array<{ length_point?: number | null; value?: number | null; unit?: string }>;
  roll_width_images?: string[];
  bow_skew_images?: string[];
  // resolved supplier/fabric names supplied by caller
  supplier_name?: string;
  fabric_type_name?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dash = (v: any): string => {
  if (v === null || v === undefined) return '—';
  const str = String(v).trim();
  return str.length === 0 ? '—' : str;
};

const compositionText = (comp: any): string => {
  if (Array.isArray(comp)) return comp.map((c: any) => `${c.percentage}% ${c.material}`).join(', ');
  if (comp && typeof comp === 'object') return JSON.stringify(comp);
  return dash(comp);
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  // Fixed header
  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold' },
  titleLine2: { fontSize: 9, color: '#555555' },
  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },

  // Result summary row
  resultRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000000', marginBottom: 12 },
  resultBox: { paddingHorizontal: 12, paddingVertical: 8, borderRightWidth: 1, borderRightColor: '#aaaaaa', justifyContent: 'center', minWidth: 120 },
  resultLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2 },
  resultValue: { fontSize: 20, fontFamily: 'Helvetica-Bold' },
  statBox: { flex: 1, paddingVertical: 8, paddingHorizontal: 6, borderRightWidth: 0.5, borderRightColor: '#cccccc', alignItems: 'center', justifyContent: 'center' },
  statLabel: { fontSize: 7.5, color: '#666666', textAlign: 'center', marginBottom: 2 },
  statValue: { fontSize: 14, fontFamily: 'Helvetica-Bold', textAlign: 'center' },

  // Gray section bar
  sectionHeader: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6, marginTop: 6 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold' },

  // Sub-bar
  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  // Info grid (3-up)
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 4, paddingHorizontal: 2 },
  infoCell: { width: '33.33%', paddingRight: 10, marginBottom: 8 },
  infoLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 1, textTransform: 'uppercase' },
  infoValue: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  // Stat boxes (2-col or 3-col)
  statGridRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 4, marginBottom: 8 },
  statGridBox: { flex: 1, borderWidth: 0.5, borderColor: '#cccccc', backgroundColor: '#f8f8f8', padding: 8 },
  statGridLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2, textTransform: 'uppercase' },
  statGridValue: { fontSize: 13, fontFamily: 'Helvetica-Bold' },

  // Samples table
  table: { borderWidth: 0.5, borderColor: '#cccccc', marginBottom: 8, marginHorizontal: 4 },
  tableHead: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 0.5, borderBottomColor: '#cccccc' },
  tableRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: '#eeeeee' },
  th: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 3 },
  thNarrow: { width: 60, fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 3, textAlign: 'center' },
  td: { flex: 1, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3 },
  tdNarrow: { width: 60, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3, textAlign: 'center' },
  tdRight: { flex: 1, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3, textAlign: 'right' },

  // Photo tiles
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  photoTile: { width: 120 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 120, height: 90, objectFit: 'cover' },

  // Checklist
  checkRow: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 0.5, borderBottomColor: '#eeeeee', paddingVertical: 4, paddingHorizontal: 6 },
  checkTitle: { fontSize: 8.5, flex: 1, marginRight: 8 },
  checkBadge: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 50, textAlign: 'right' },
  checkRemarks: { fontSize: 8, color: '#444444', fontStyle: 'italic', paddingHorizontal: 6, paddingBottom: 3 },
  checkImgRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingHorizontal: 6, paddingBottom: 6 },
  checkImg: { width: 80, height: 60, objectFit: 'cover' },

  // Defect entry
  defectBar: { backgroundColor: '#fff0f0', borderBottomWidth: 0.5, borderBottomColor: '#ffcccc', paddingHorizontal: 8, paddingVertical: 5, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  defectBarLeft: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  defectBarRight: { fontSize: 8.5, color: '#cc0000', fontFamily: 'Helvetica-Bold' },
  defectMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, paddingHorizontal: 6, paddingVertical: 4 },
  defectMetaItem: { flexDirection: 'row', gap: 3 },
  defectMetaLabel: { fontSize: 8, color: '#666666' },
  defectMetaValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
  defectDesc: { fontSize: 8.5, color: '#333333', fontStyle: 'italic', paddingHorizontal: 6, paddingBottom: 4 },
  defectImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 6, paddingBottom: 6 },
  defectImg: { width: 120, height: 90, objectFit: 'cover' },

  // 4-point summary
  summaryGrid: { flexDirection: 'row', gap: 8, paddingHorizontal: 4, marginBottom: 10 },
  summaryBox: { flex: 1, borderWidth: 0.5, borderColor: '#cccccc', backgroundColor: '#f8f8f8', padding: 8 },

  // Remarks + signatures
  remarksText: { fontSize: 9, paddingHorizontal: 4, paddingVertical: 6, minHeight: 36 },
  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16, paddingHorizontal: 4 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigEmpty: { height: 44, marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },

  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  // Footer
  pageFooter: {
    position: 'absolute', bottom: 14, left: 36, right: 36,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerSide: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Sub-components ──────────────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Text style={s.logoText}>QA MATE</Text>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>FABRIC INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerSide} />
    <Text style={s.footerCenter} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}><Text style={s.sectionHeaderText}>{title}</Text></View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}><Text style={s.subHeaderText}>{title}</Text></View>
);

const InfoCell = ({ label, value }: { label: string; value: string }) => (
  <View style={s.infoCell}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value}</Text>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
const FabricInspectionReportPDF = ({ report }: { report: FabricReportPDFData }) => {
  const isPass = report.result === 'PASS';
  const reportId = `QFB ${report.id.slice(0, 6).toUpperCase()}`;

  const gsmSamples = report.gsm_samples || report.gsmSamples || [];
  let avgGsm = 0;
  if (gsmSamples.length > 0) {
    let total = 0; let count = 0;
    for (const s of gsmSamples) {
      const v = Number(s.original_gsm ?? s.originalGsm ?? 0);
      if (v > 0) { total += v; count++; }
    }
    if (count > 0) avgGsm = total / count;
  }
  const targetGsm = Number(report.target_gsm) || 0;
  const gsmVariation = targetGsm > 0 ? ((avgGsm - targetGsm) / targetGsm) * 100 : 0;

  const tEpi = Number(report.target_epi || 0);
  const oEpi = Number(report.original_epi || 0);
  const tPpi = Number(report.target_ppi || 0);
  const oPpi = Number(report.original_ppi || 0);
  const epiVar = tEpi > 0 ? ((oEpi - tEpi) / tEpi) * 100 : 0;
  const ppiVar = tPpi > 0 ? ((oPpi - tPpi) / tPpi) * 100 : 0;
  const epVariation = Math.abs(epiVar) > Math.abs(ppiVar) ? epiVar : ppiVar;

  const cuttableWidth = (Number(report.width) > 0)
    ? (Number(report.width) - Number(report.selvedge || 0)).toFixed(2)
    : null;

  const ptsPerHundred = report.total_yards && report.width
    ? ((report.total_penalty_points * 3600) / (report.total_yards * (report.width || 60))).toFixed(1)
    : '—';

  const hasDimVerif =
    (report.roll_width_samples && report.roll_width_samples.length > 0) ||
    (report.bow_skew_samples && report.bow_skew_samples.length > 0) ||
    (report.roll_width_images && report.roll_width_images.length > 0) ||
    (report.bow_skew_images && report.bow_skew_images.length > 0);

  const docPhotos = (report.photos || []).filter(p => p.image_url);

  return (
    <Document title={`Fabric Inspection Report - ${report.po_number || report.id}`}>

      {/* ═══ PAGE 1 — Result + Basic Details + Inspection Details ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        {/* Result summary row */}
        <View style={s.resultRow}>
          <View style={s.resultBox}>
            <Text style={s.resultLabel}>INSPECTION RESULT</Text>
            <Text style={s.resultValue}>{isPass ? 'PASS' : 'FAIL'}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Total Penalty Points</Text>
            <Text style={s.statValue}>{report.total_penalty_points || 0}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Defect Count</Text>
            <Text style={s.statValue}>{(report.defects || []).length}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Rolls Inspected</Text>
            <Text style={s.statValue}>{report.rolls_inspected || 0}</Text>
          </View>
          <View style={[s.statBox, { borderRightWidth: 0 }]}>
            <Text style={s.statLabel}>Report ID</Text>
            <Text style={[s.statValue, { fontSize: 10 }]}>{reportId}</Text>
          </View>
        </View>

        {/* Basic Details */}
        <SectionBar title="BASIC DETAILS" />
        <View style={s.infoGrid}>
          <InfoCell label="Date of Inspection" value={formatDate(report.created_at)} />
          <InfoCell label="Inspector Name" value={dash(report.inspector_name)} />
          <InfoCell label="Factory / Supplier" value={dash(report.supplier_name || report.supplier)} />
          <InfoCell label="Buyer / Client" value={dash(report.buyer_name)} />
          <InfoCell label={`${(report.inspection_basis || 'PO').toUpperCase()} Number`} value={dash(report.po_number)} />
          <InfoCell label="Style / Ref Number" value={dash(report.style_number)} />
          <InfoCell label="Fabric Type" value={dash(report.fabric_type_name || report.fabric_type)} />
          <InfoCell label="Color Shade" value={dash(report.color_shade)} />
          <InfoCell label="Lot Size" value={dash(report.lot_size)} />
          <InfoCell label="Sample Size" value={dash(report.sample_size)} />
          <InfoCell label="Total Yards / Meters" value={report.total_yards ? `${report.total_yards} yds` : '—'} />
        </View>

        {/* Inspection Details */}
        <SectionBar title="INSPECTION DETAILS" />
        <View style={s.infoGrid}>
          <InfoCell label="Composition" value={compositionText(report.composition)} />
          <InfoCell label="Pantone" value={report.pantone?.code ? `${report.pantone.code} (${report.pantone.name})` : dash(report.pantone)} />
          <InfoCell label="Selvedge" value={dash(report.selvedge)} />
          <InfoCell label="Length" value={dash(report.length)} />
          <InfoCell label="Width" value={dash(report.width)} />
          <InfoCell label="Cuttable Width" value={cuttableWidth || '—'} />
          <InfoCell label="AQL Points" value={dash(report.aql_points)} />
          <InfoCell label="Inspection Standard" value={dash(report.inspection_standard)} />
        </View>

        <PageFooter />
      </Page>

      {/* ═══ DIMENSION VERIFICATION (conditional) ═══ */}
      {hasDimVerif && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="DIMENSION VERIFICATION" />

          {/* Roll Width */}
          <SubBar title="ROLL WIDTH SAMPLES" />
          {report.roll_width_samples && report.roll_width_samples.length > 0 ? (
            <View style={s.table}>
              <View style={s.tableHead}>
                <Text style={s.thNarrow}>#</Text>
                <Text style={s.th}>At (m)</Text>
                <Text style={s.th}>Value</Text>
              </View>
              {report.roll_width_samples.map((r, i) => (
                <View key={i} style={s.tableRow}>
                  <Text style={s.tdNarrow}>{i + 1}</Text>
                  <Text style={s.td}>{r.length_point != null ? `${r.length_point} m` : '—'}</Text>
                  <Text style={s.td}>{r.value != null ? `${r.value} ${r.unit || 'in'}` : '—'}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={s.noDataText}>No samples recorded.</Text>
          )}
          {report.roll_width_images && report.roll_width_images.length > 0 && (
            <View style={s.photoGrid}>
              {report.roll_width_images.filter(u => u).map((u, i) => (
                <View key={i} style={s.photoTile}>
                  <Text style={s.photoLabel}>Roll Width {i + 1}</Text>
                  <Image src={u} style={s.photoImg} />
                </View>
              ))}
            </View>
          )}

          {/* Bow & Skew */}
          <SubBar title="BOW & SKEW SAMPLES" />
          {report.bow_skew_samples && report.bow_skew_samples.length > 0 ? (
            <View style={s.table}>
              <View style={s.tableHead}>
                <Text style={s.thNarrow}>#</Text>
                <Text style={s.th}>At (m)</Text>
                <Text style={s.th}>Value</Text>
              </View>
              {report.bow_skew_samples.map((r, i) => (
                <View key={i} style={s.tableRow}>
                  <Text style={s.tdNarrow}>{i + 1}</Text>
                  <Text style={s.td}>{r.length_point != null ? `${r.length_point} m` : '—'}</Text>
                  <Text style={s.td}>{r.value != null ? `${r.value} ${r.unit || 'in'}` : '—'}</Text>
                </View>
              ))}
            </View>
          ) : (
            <Text style={s.noDataText}>No samples recorded.</Text>
          )}
          {report.bow_skew_images && report.bow_skew_images.length > 0 && (
            <View style={s.photoGrid}>
              {report.bow_skew_images.filter(u => u).map((u, i) => (
                <View key={i} style={s.photoTile}>
                  <Text style={s.photoLabel}>Bow & Skew {i + 1}</Text>
                  <Image src={u} style={s.photoImg} />
                </View>
              ))}
            </View>
          )}

          <PageFooter />
        </Page>
      )}

      {/* ═══ WEIGHT + ENDS & PICKS ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        {/* Weight Details */}
        <SectionBar title="WEIGHT DETAILS" />
        <View style={s.statGridRow}>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>Target GSM</Text>
            <Text style={s.statGridValue}>{targetGsm > 0 ? targetGsm : '—'}</Text>
          </View>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>Average GSM</Text>
            <Text style={s.statGridValue}>{avgGsm > 0 ? avgGsm.toFixed(2) : '—'}</Text>
          </View>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>Variation</Text>
            <Text style={s.statGridValue}>{avgGsm > 0 ? `${gsmVariation > 0 ? '+' : ''}${gsmVariation.toFixed(2)}%` : '—'}</Text>
          </View>
        </View>

        {gsmSamples.length > 0 && (
          <>
            <SubBar title="WEIGHT SAMPLES" />
            <View style={s.table}>
              <View style={s.tableHead}>
                <Text style={s.thNarrow}>#</Text>
                <Text style={s.th}>Sample Size</Text>
                <Text style={s.th}>Original GSM</Text>
              </View>
              {gsmSamples.map((gs, i) => {
                const sSize = gs.sample_size ?? gs.sampleSize;
                const oGsm = Number(gs.original_gsm ?? gs.originalGsm ?? 0);
                return (
                  <View key={i} style={s.tableRow}>
                    <Text style={s.tdNarrow}>{i + 1}</Text>
                    <Text style={s.td}>{sSize != null ? String(sSize) : '—'}</Text>
                    <Text style={s.td}>{oGsm > 0 ? `${oGsm} GSM` : '—'}</Text>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Ends & Picks */}
        <SectionBar title="ENDS & PICKS DETAILS" />
        <View style={s.statGridRow}>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>EPI (Target / Actual)</Text>
            <Text style={s.statGridValue}>{tEpi} / {oEpi}</Text>
          </View>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>PPI (Target / Actual)</Text>
            <Text style={s.statGridValue}>{tPpi} / {oPpi}</Text>
          </View>
          <View style={s.statGridBox}>
            <Text style={s.statGridLabel}>Variation</Text>
            <Text style={s.statGridValue}>{(tEpi > 0 || tPpi > 0) ? `${epVariation > 0 ? '+' : ''}${epVariation.toFixed(2)}%` : '—'}</Text>
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ CHECKLIST RESULTS ═══ */}
      {(report.checklists || []).length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="CHECKLIST RESULTS" />
          {report.checklists.map((item, idx) => {
            const statusLabel = (item.status || '').toUpperCase();
            const hasRemarks = !!(item.remarks && item.remarks.trim().length > 0);
            const hasImgs = !!(item.evidence_urls && item.evidence_urls.length > 0);
            return (
              <View key={idx}>
                <View style={s.checkRow}>
                  <Text style={s.checkTitle}>{item.title}</Text>
                  <Text style={s.checkBadge}>{statusLabel}</Text>
                </View>
                {hasRemarks && <Text style={s.checkRemarks}>Remarks: {item.remarks}</Text>}
                {hasImgs && (
                  <View style={s.checkImgRow}>
                    {item.evidence_urls.filter(u => u).map((u, i) => (
                      <Image key={i} src={u} style={s.checkImg} />
                    ))}
                  </View>
                )}
              </View>
            );
          })}
          <PageFooter />
        </Page>
      )}

      {/* ═══ DEFECT LOG ═══ */}
      {(report.defects || []).length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="DEFECT LOG" />
          {report.defects.map((log, idx) => (
            <View key={idx}>
              <View style={s.defectBar}>
                <Text style={s.defectBarLeft}>Roll #{log.roll_number} — {log.type}</Text>
                <Text style={s.defectBarRight}>Penalty: {log.points} pts | Yard: {log.yard_number}</Text>
              </View>
              <View style={s.defectMeta}>
                {log.category ? (
                  <View style={s.defectMetaItem}><Text style={s.defectMetaLabel}>Category:</Text><Text style={s.defectMetaValue}>{log.category}</Text></View>
                ) : null}
                {log.defect_type ? (
                  <View style={s.defectMetaItem}><Text style={s.defectMetaLabel}>Type:</Text><Text style={s.defectMetaValue}>{log.defect_type}</Text></View>
                ) : null}
              </View>
              {log.description ? <Text style={s.defectDesc}>"{log.description}"</Text> : null}
              {(log.image_urls || []).filter(u => u).length > 0 && (
                <View style={s.defectImgRow}>
                  {log.image_urls.filter(u => u).map((u, i) => (
                    <Image key={i} src={u} style={s.defectImg} />
                  ))}
                </View>
              )}
            </View>
          ))}
          <PageFooter />
        </Page>
      )}

      {/* ═══ DOCUMENTATION PHOTOS ═══ */}
      {docPhotos.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="DOCUMENTATION PHOTOS" />
          <View style={s.photoGrid}>
            {docPhotos.map((p, i) => (
              <View key={p.id || i} style={s.photoTile}>
                <Text style={s.photoLabel}>{p.label || 'Photo'}</Text>
                <Image src={p.image_url} style={s.photoImg} />
              </View>
            ))}
          </View>
          <PageFooter />
        </Page>
      )}

      {/* ═══ SUMMARY + REMARKS + SIGNATURES (final) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        {/* 4 Point Inspection Summary */}
        <SectionBar title="4 POINT INSPECTION SUMMARY" />
        <View style={s.summaryGrid}>
          <View style={s.summaryBox}>
            <Text style={s.statGridLabel}>Total Yards Inspected</Text>
            <Text style={[s.statGridValue, { fontSize: 15 }]}>{report.total_yards || 0} <Text style={{ fontSize: 9, fontFamily: 'Helvetica' }}>yds</Text></Text>
          </View>
          <View style={s.summaryBox}>
            <Text style={s.statGridLabel}>Total Penalty Points</Text>
            <Text style={[s.statGridValue, { fontSize: 15 }]}>{report.total_penalty_points || 0} <Text style={{ fontSize: 9, fontFamily: 'Helvetica' }}>pts</Text></Text>
          </View>
          <View style={s.summaryBox}>
            <Text style={s.statGridLabel}>Avg pts / 100 Sq. Yds</Text>
            <Text style={[s.statGridValue, { fontSize: 15 }]}>{ptsPerHundred}</Text>
          </View>
          <View style={s.summaryBox}>
            <Text style={s.statGridLabel}>Acceptance Standard</Text>
            <Text style={{ fontSize: 8.5, fontFamily: 'Helvetica-Bold', marginTop: 4 }}>Max 40 pts / 100 sq. yds</Text>
            <View style={{ marginTop: 6, height: 5, backgroundColor: '#e2e8f0' }}>
              <View style={{ height: 5, backgroundColor: (report.total_penalty_points || 0) > 40 ? '#ef4444' : '#10b981', width: `${Math.min(100, ((report.total_penalty_points || 0) / 40) * 100)}%` }} />
            </View>
          </View>
        </View>

        {/* Inspector's Remarks */}
        <SectionBar title="INSPECTOR'S REMARKS" />
        <Text style={s.remarksText}>{report.overall_remarks || '—'}</Text>

        {/* Signatures */}
        <View style={{ marginTop: 12 }}>
          <SectionBar title="AUTHORIZATION" />
          <View style={s.sigRow}>
            <View style={s.sigBox}>
              <Text style={s.sigLabel}>Factory Rep Signature</Text>
              {report.factory_signature_url ? (
                <Image src={report.factory_signature_url} style={s.sigImg} />
              ) : (
                <View style={s.sigEmpty} />
              )}
              <Text style={s.sigName}>Factory Quality Manager</Text>
              <Text style={s.sigRole}>Factory Rep</Text>
            </View>
            <View style={s.sigBox}>
              <Text style={s.sigLabel}>QA Inspector Signature</Text>
              {report.qa_signature_url ? (
                <Image src={report.qa_signature_url} style={s.sigImg} />
              ) : (
                <View style={s.sigEmpty} />
              )}
              <Text style={s.sigName}>{dash(report.inspector_name)}</Text>
              <Text style={s.sigRole}>QA Inspector</Text>
            </View>
            <View style={[s.sigBox, { flex: 0.7 }]}>
              <Text style={s.sigLabel}>Date Signed</Text>
              <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
                <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>{formatDate(report.created_at)}</Text>
              </View>
              <Text style={s.sigRole}>Date of Inspection</Text>
            </View>
          </View>
        </View>

        {/* Brand footer */}
        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text style={{ fontSize: 7.5, color: '#555555', flex: 1, textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2563eb' }}>QAMATE</Text>
            <Text style={{ fontSize: 7.5, color: '#555555' }}>info@qamate.in | www.qamate.in</Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default FabricInspectionReportPDF;
